
import { Component, Emit, Prop, Vue, Watch } from "vue-property-decorator";
import { ActionPlanTask } from "@/store/tasks/types";
import EventOutcomeSelector from "@/components/EventOutcomeSelector.vue";
import { namespace } from "vuex-class";
import { requiredIf } from "vuelidate/lib/validators";
import { validationMixin } from "vuelidate";
import { EventOutcomeResponse, EventOutcome } from "@/types/EventOutcomeDTO";

const SiteMonitorStore = namespace("siteMonitor");

@Component({
	components: {
		"event-outcome-select": EventOutcomeSelector
	},
	mixins: [validationMixin],
	validations: {
		eventOutcomeResult: {
			required: requiredIf(function() {
				return this.task.required;
			}),
		}
	}
})
export default class EventOutcomeTask extends Vue{
	@SiteMonitorStore.Getter("eventOutcomesFlatList") outcomesFlatList: EventOutcome[];

	@SiteMonitorStore.Action private loadOutcomesForEvent: (eventId: number) => Promise<EventOutcomeResponse>;

	@Prop({ required: true, type: Boolean, default: false})
	public editing!: boolean;

	@Prop({ required: true, type: Object})
	public task!: ActionPlanTask;

	@Prop({type: Number, default: 0})
	private eventId: number

	private eventOutcomeId: number = 0;
	private eventOutcomePath: string = "";

	private clearClicked: boolean = false;

	private dataLoaded: boolean = false;

	@Emit("complete-task")
	public onCompleteTask(): ActionPlanTask {
		return {
			...this.task,
			result: this.eventOutcomePath,
			taskData: this.eventOutcomeId,
			completed: true
		};
	}

	public async mounted(): Promise<void> {
		if (this.eventId) {
			await this.loadOutcomesForEvent(this.eventId);
			this.dataLoaded = true;
		}
	}

	public get eventCategoriesMap(): { id: number; title: string; parentId: number }[] {
		if (!this.outcomesFlatList) {
			return [];
		}

		return this.outcomesFlatList.map((category) => {
			return {
				id: category.eventOutcomeID,
				parentId: category.parentID,
				title: category.title,
			};
		});
	}

	private outcomeChanged(value: number): void {
		this.eventOutcomeId = value;
	}

	private outcomePathChanged(value: string): void {
		this.eventOutcomePath = value;
	}

	private clearOutcome(): void {
		this.clearClicked = true;
		this.eventOutcomeId = 0;
		this.eventOutcomePath = "";
	}

	private outcomeCleared(): void {
		this.clearClicked = false;
	}

	/**
	 * If the selected event outcome is a leaf node (i.e. it has no children)
	 * @private
	 */
	private get isLeafNode(): boolean {
		return this.eventOutcomeId != 0 &&
			this.eventCategoriesMap.filter(outcome => outcome.parentId === this.eventOutcomeId).length == 0
	}

	/**
	 * Valid if we've selected an outcome and it's a leaf outcome
	 * @private
	 */
	private get validEventOutcome(): boolean {
		return this.isLeafNode;
	}

	@Watch("task.completed")
	private onTaskReOpened(value: boolean, oldValue: boolean){
		if (!value && oldValue) {
			this.eventOutcomeId = 0;
		}
	}
}
